<script setup>
import { ref, onMounted } from 'vue'
import { Config } from '@/Config'
import { useI18n } from 'vue-i18n'
import * as Server from '@/services/Server'
import { useMainStore } from '@/stores/main'
import PageHeader from '@/components/PageHeader.vue'
import FastestLapStandings from '@/components/FastestLapStandings.vue'
import TrackTitle from '@/components/TrackTitle.vue'
import { podiumClass } from '@/lib/podium'

const { t } = useI18n()
const store = useMainStore()

const results = ref({})
const standings = ref(null)
const loading = ref(true)

onMounted(() => {
  Server.get(Config.server.routes.api.leaderboard_summary)
    .then(response => {
      results.value = response.data?.tracks ?? {}
      standings.value = response.data?.standings ?? null
    })
    .catch(() => {
      results.value = {}
      standings.value = null
    })
    .finally(() => {
      loading.value = false
    })
})
</script>

<template>
  <main>
    <v-container>
      <PageHeader
        :title="$t('leaderboard.title')"
        :subtitle="$t('leaderboard.subtitle')"
        icon="mdi-podium"
      />

      <v-alert
        type="warning"
        variant="tonal"
        class="mb-4"
        :text="$t('leaderboard.developmentWarning')"
      />
      
      <v-row>
        <v-col
          v-for="track in store.tracks"
          :key="track.id"
          cols="12"
          sm="6"
          md="4"
        >
          <v-card
            :to="{ name: 'track', params: { trackId: track.id } }"
            hover
            elevation="3"
            rounded="lg"
            class="track-card h-100 d-flex flex-column"
          >
            <div class="track-card-accent" />

            <v-card-item class="pa-4">
              <template v-slot:prepend>
                <v-avatar color="primary" variant="tonal" size="48">
                  <v-icon size="28">mdi-flag-checkered</v-icon>
                </v-avatar>
              </template>
              <TrackTitle :track-id="track.id" variant="stacked" class="text-h6" />
            </v-card-item>

            <v-card-text class="pt-0">
              <v-divider class="mb-3" />
              <div v-if="loading" class="d-flex align-center ga-2 text-medium-emphasis">
                <v-progress-circular indeterminate size="20" width="2" />
                <span class="text-caption">{{ $t('leaderboard.loading') }}</span>
              </div>
              <div v-else-if="results[track.id]?.length" class="d-flex flex-column ga-1">
                <div
                  v-for="entry in results[track.id]"
                  :key="`${track.id}-${entry.position}`"
                  class="d-flex align-center ga-2"
                >
                  <span class="rank-number" :class="podiumClass(entry.position)">
                    {{ entry.position }}
                  </span>
                  <span class="font-weight-medium text-truncate flex-grow-1">{{ entry.driver_name }}</span>
                  <v-chip size="x-small" variant="tonal" prepend-icon="mdi-car-sports">
                    {{ $t(`cars.${entry.car_id}`) }}
                  </v-chip>
                  <span class="font-weight-bold text-mono text-primary">{{ entry.lap_time }}</span>
                </div>
              </div>
              <div v-else class="d-flex align-center ga-2 text-medium-emphasis">
                <v-icon size="20">mdi-trophy-outline</v-icon>
                <span class="text-caption">{{ $t('leaderboard.noRecordYet') }}</span>
              </div>
            </v-card-text>

            <v-spacer />

            <v-card-actions>
              <v-spacer />
              <v-btn
                variant="text"
                color="primary"
                append-icon="mdi-chevron-right"
                size="small"
              >
                {{ $t('leaderboard.viewTimes') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <FastestLapStandings
        class="mt-5"
        :standings="standings"
        :loading="loading"
      />

    </v-container>
  </main>
</template>

<style scoped>
.track-card {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  overflow: hidden;
}
.track-card:hover {
  transform: translateY(-4px);
}
.track-card-accent {
  height: 4px;
  background: linear-gradient(90deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
}
.text-mono {
  font-family: monospace;
  font-size: 0.85rem;
}
.rank-number {
  width: 18px;
  flex: none;
  text-align: right;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
</style>
